/**
 * Strategy Detail Dialog
 *
 * Comprehensive dialog for visualizing strategy execution details including:
 * - Overview with P&L summary
 * - Spot orders (buy/sell)
 * - Futures orders (open/close short)
 * - Funding history
 * - P&L audit with verification
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BuildIcon from '@mui/icons-material/Build';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { exportStrategyDetail } from '../../../utils/strategyExport';

import type { StrategyInfo } from '../../../types/strategy';
import { getStateColor, getStateDisplayName, getStrategyTypeColor } from '../../../types/strategy';
import { traderTheme as theme } from '../traderDashboardTheme';
import { useTraderData } from '../../../contexts/TraderDataContext';
import {
  buildTraderApiBasePath,
  getTraderRouteVersion,
} from '../../../utils/traderRouting';

import { OverviewTab } from './OverviewTab';
import { SpotOrdersTab } from './SpotOrdersTab';
import { FuturesOrdersTab } from './FuturesOrdersTab';
import { FundingHistoryTab } from './FundingHistoryTab';
import { PnLAuditTab } from './PnLAuditTab';

export interface StrategyDetailDialogProps {
  open: boolean;
  strategyId: string | null;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`strategy-detail-tabpanel-${index}`}
      aria-labelledby={`strategy-detail-tab-${index}`}
      {...other}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export const StrategyDetailDialog: React.FC<StrategyDetailDialogProps> = ({
  open,
  strategyId,
  onClose,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    traderName,
    excludeStrategy,
    recoverMissingOrders,
  } = useTraderData();
  const [tabValue, setTabValue] = useState(0);
  const [strategy, setStrategy] = useState<StrategyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverDialogOpen, setRecoverDialogOpen] = useState(false);
  const [excluding, setExcluding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const traderApiBasePath = traderName
    ? buildTraderApiBasePath(traderName, getTraderRouteVersion(location.pathname))
    : null;

  // Export handlers
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCsv = () => {
    if (strategy) {
      exportStrategyDetail(strategy, 'csv');
    }
    handleExportClose();
  };

  const handleExportXlsx = () => {
    if (strategy) {
      exportStrategyDetail(strategy, 'xlsx');
    }
    handleExportClose();
  };

  // Fetch strategy details
  const fetchStrategy = useCallback(async () => {
    if (!strategyId || !traderApiBasePath) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${traderApiBasePath}/strategies/${strategyId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch strategy: ${response.statusText}`);
      }
      const data = await response.json();
      setStrategy(data.strategy);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch strategy details');
    } finally {
      setLoading(false);
    }
  }, [strategyId, traderApiBasePath]);

  // Fetch on open
  useEffect(() => {
    if (open && strategyId) {
      fetchStrategy();
    }
  }, [open, strategyId, fetchStrategy]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setStrategy(null);
      setError(null);
    }
  }, [open]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCopyId = useCallback(() => {
    if (strategyId) {
      navigator.clipboard.writeText(strategyId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [strategyId]);

  // Repair strategy (reconciliation)
  const handleRepair = useCallback(async () => {
    if (!strategyId || !traderApiBasePath) return;

    setRepairing(true);
    setError(null);

    try {
      const response = await fetch(`${traderApiBasePath}/strategies/${strategyId}/repair`, {
        method: 'POST',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `Failed to repair: ${response.statusText}`);
      }
      // Wait a bit for reconciliation to complete, then refresh
      setTimeout(() => {
        fetchStrategy();
        setRepairing(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to repair strategy');
      setRepairing(false);
    }
  }, [strategyId, traderApiBasePath, fetchStrategy]);

  const handleRecover = useCallback(async () => {
    if (!strategyId) return;

    setRecovering(true);
    setError(null);

    try {
      const success = await recoverMissingOrders(strategyId);
      if (!success) {
        throw new Error(t('traderDashboard.strategyDetail.recoverFailed'));
      }

      setRecoverDialogOpen(false);
      setTimeout(() => {
        fetchStrategy();
        setRecovering(false);
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('traderDashboard.strategyDetail.recoverFailed'),
      );
      setRecovering(false);
    }
  }, [strategyId, recoverMissingOrders, fetchStrategy, t]);

  const handleExclude = useCallback(async () => {
    if (!strategyId) return;

    setExcluding(true);
    setError(null);

    try {
      const success = await excludeStrategy(strategyId);
      if (!success) {
        throw new Error(t('traderDashboard.strategyDetail.excludeFailed'));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('traderDashboard.strategyDetail.excludeFailed'));
    } finally {
      setExcluding(false);
    }
  }, [strategyId, excludeStrategy, onClose, t]);

  // Computed values
  const baseCurrency = useMemo(() => {
    return strategy?.execution_context.opportunity?.base || '-';
  }, [strategy]);

  const spotOrdersCount = useMemo(() => {
    return Object.keys(strategy?.execution_context.orders || {}).length;
  }, [strategy]);

  const futuresOrdersCount = useMemo(() => {
    return Object.keys(strategy?.execution_context.futures_orders || {}).length;
  }, [strategy]);

  const hasIncomeHistory = useMemo(() => {
    return (strategy?.execution_context.pnl_snapshot?.fetched?.income_history?.length ?? 0) > 0;
  }, [strategy]);

  const canExclude = useMemo(() => {
    if (!strategy) return false;
    return !strategy.is_running || strategy.is_monitoring_paused;
  }, [strategy]);

  const canRecoverMissingOrders = useMemo(() => {
    if (!strategy) return false;
    return strategy.current_state === 'Standby' || strategy.current_state === 'BuyOpen';
  }, [strategy]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.bg.primary,
          backgroundImage: 'none',
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.secondary,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          py: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: theme.text.primary, fontWeight: 600 }}
            >
              {t('traderDashboard.strategyDetail.title', { symbol: baseCurrency })}
            </Typography>
            {strategy && (
              <>
                <Chip
                  label={strategy.strategy_type}
                  size="small"
                  sx={{
                    backgroundColor: `${getStrategyTypeColor(strategy.strategy_type)}20`,
                    color: getStrategyTypeColor(strategy.strategy_type),
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
                <Chip
                  label={getStateDisplayName(strategy.current_state)}
                  size="small"
                  sx={{
                    backgroundColor: `${getStateColor(strategy.current_state)}20`,
                    color: getStateColor(strategy.current_state),
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </>
            )}
          </Box>
          {strategyId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.text.secondary,
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                }}
              >
                ID: {strategyId.slice(0, 8)}...{strategyId.slice(-4)}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyId}
                sx={{ p: 0.25, color: copied ? theme.profit : theme.text.secondary }}
              >
                <ContentCopyIcon sx={{ fontSize: 12 }} />
              </IconButton>
              {strategy && (
                <>
                  {strategy.execution_context.opportunity?.detected_at && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.text.muted, ml: 2, fontSize: '0.7rem' }}
                    >
                      {t('traderDashboard.strategyDetail.detectedAt')}: {formatDate(strategy.execution_context.opportunity.detected_at)}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ color: theme.text.muted, ml: 2, fontSize: '0.7rem' }}
                  >
                    {t('traderDashboard.strategyDetail.startedAt')}: {formatDate(strategy.started_at)}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('traderDashboard.strategyDetail.export')}>
            <IconButton
              onClick={handleExportClick}
              disabled={loading || !strategy}
              size="small"
              sx={{ color: theme.text.secondary }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                backgroundColor: theme.bg.secondary,
                border: `1px solid ${theme.border}`,
              },
            }}
          >
            <MenuItem
              onClick={handleExportCsv}
              sx={{ color: theme.text.primary, fontSize: '0.85rem' }}
            >
              {t('traderDashboard.export.csv')}
            </MenuItem>
            <MenuItem
              onClick={handleExportXlsx}
              sx={{ color: theme.text.primary, fontSize: '0.85rem' }}
            >
              {t('traderDashboard.export.xlsx')}
            </MenuItem>
          </Menu>
          <Tooltip title={t('traderDashboard.strategyDetail.exclude')}>
            <span>
              <IconButton
                onClick={handleExclude}
                disabled={excluding || loading || !canExclude}
                size="small"
                sx={{ color: theme.text.secondary }}
              >
                {excluding ? (
                  <CircularProgress size={16} sx={{ color: theme.text.secondary }} />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('traderDashboard.strategyDetail.repair')}>
            <IconButton
              onClick={handleRepair}
              disabled={repairing || loading}
              size="small"
              sx={{ color: theme.text.secondary }}
            >
              {repairing ? (
                <CircularProgress size={16} sx={{ color: theme.accent }} />
              ) : (
                <BuildIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {canRecoverMissingOrders && (
            <Tooltip title={t('traderDashboard.strategyDetail.recover')}>
              <IconButton
                onClick={() => setRecoverDialogOpen(true)}
                disabled={recovering || loading}
                size="small"
                sx={{ color: theme.text.secondary }}
              >
                {recovering ? (
                  <CircularProgress size={16} sx={{ color: theme.accent }} />
                ) : (
                  <RestartAltIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: theme.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.loss }}>
              {error}
            </Typography>
          </Box>
        )}

        {loading && !strategy && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: theme.accent }} />
          </Box>
        )}

        {strategy && (
          <>
            <Box sx={{ borderBottom: `1px solid ${theme.border}` }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    color: theme.text.secondary,
                    fontSize: '0.85rem',
                    '&.Mui-selected': {
                      color: theme.accent,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.accent,
                  },
                }}
              >
                <Tab label={t('traderDashboard.strategyDetail.tabs.overview')} />
                <Tab label={`${t('traderDashboard.strategyDetail.tabs.spotOrders')} (${spotOrdersCount})`} />
                <Tab label={`${t('traderDashboard.strategyDetail.tabs.futuresOrders')} (${futuresOrdersCount})`} />
                <Tab
                  label={t('traderDashboard.strategyDetail.tabs.fundingHistory')}
                  disabled={!hasIncomeHistory && !strategy.execution_context.funding_fee}
                />
                <Tab label={t('traderDashboard.strategyDetail.tabs.pnlAudit')} />
              </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
              <TabPanel value={tabValue} index={0}>
                <OverviewTab strategy={strategy} />
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <SpotOrdersTab strategy={strategy} />
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <FuturesOrdersTab strategy={strategy} />
              </TabPanel>
              <TabPanel value={tabValue} index={3}>
                <FundingHistoryTab strategy={strategy} />
              </TabPanel>
              <TabPanel value={tabValue} index={4}>
                <PnLAuditTab strategy={strategy} />
              </TabPanel>
            </Box>
          </>
        )}
      </DialogContent>

      <Dialog
        open={recoverDialogOpen}
        onClose={() => !recovering && setRecoverDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: theme.text.primary }}>
          {t('traderDashboard.recoverMissingOrdersConfirm.title', { symbol: baseCurrency })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.text.secondary }}>
            {t('traderDashboard.recoverMissingOrdersConfirm.confirmMessage')}
          </DialogContentText>
          <Box component="ul" sx={{ color: theme.text.secondary, pl: 3, mb: 0 }}>
            <li>{t('traderDashboard.recoverMissingOrdersConfirm.checksSpot')}</li>
            <li>{t('traderDashboard.recoverMissingOrdersConfirm.checksFutures')}</li>
            <li>{t('traderDashboard.recoverMissingOrdersConfirm.updatesTransfers')}</li>
            <li>{t('traderDashboard.recoverMissingOrdersConfirm.reloadsActive')}</li>
          </Box>
          <DialogContentText sx={{ color: theme.loss, mt: 2 }}>
            {t('traderDashboard.recoverMissingOrdersConfirm.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRecoverDialogOpen(false)}
            disabled={recovering}
            sx={{ color: theme.text.secondary }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRecover}
            variant="contained"
            disabled={recovering}
            sx={{
              backgroundColor: theme.accent,
              color: theme.bg.primary,
              '&:hover': {
                backgroundColor: theme.accentHover,
              },
            }}
          >
            {recovering
              ? t('traderDashboard.recoverMissingOrdersConfirm.recovering')
              : t('traderDashboard.recoverMissingOrdersConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default StrategyDetailDialog;
